import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Image as KonvaImage, Transformer, Rect, Group, Text } from 'react-konva';
import useImage from 'use-image';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, ArrowLeftIcon, ShoppingBagIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { API } from '../../config';
import { useAppContext } from '../../context/AppContext';
import toast from 'react-hot-toast';

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

// A wrapper for the user's uploaded image on the canvas handles selection/transform
const URLImage = ({ image, isSelected, onSelect, onChange }) => {
    const [img] = useImage(image.src, 'anonymous');
    const shapeRef = useRef();
    const trRef = useRef();

    useEffect(() => {
        if (isSelected && trRef.current && shapeRef.current) {
            trRef.current.nodes([shapeRef.current]);
            trRef.current.getLayer().batchDraw();
        }
    }, [isSelected]);

    return (
        <>
            <KonvaImage
                image={img}
                ref={shapeRef}
                {...image}
                draggable
                onClick={onSelect}
                onTap={onSelect}
                onDragEnd={(e) => {
                    onChange({
                        ...image,
                        x: e.target.x(),
                        y: e.target.y(),
                    });
                }}
                onTransformEnd={(e) => {
                    const node = shapeRef.current;
                    const scaleX = node.scaleX();
                    const scaleY = node.scaleY();

                    // we will reset it back to 1 to avoid weird scaling issues
                    node.scaleX(1);
                    node.scaleY(1);
                    onChange({
                        ...image,
                        x: node.x(),
                        y: node.y(),
                        width: Math.max(5, node.width() * scaleX),
                        height: Math.max(node.height() * scaleY),
                    });
                }}
            />
            {isSelected && (
                <Transformer
                    ref={trRef}
                    boundBoxFunc={(oldBox, newBox) => {
                        // limit resize
                        if (newBox.width < 5 || newBox.height < 5) {
                            return oldBox;
                        }
                        return newBox;
                    }}
                />
            )}
        </>
    );
};

// Helper for Overlay Image
const OverlayImage = ({ src, width, height, onLoad }) => {
    const [img] = useImage(src, 'anonymous');

    useEffect(() => {
        if (img && onLoad) {
            onLoad({ width: img.width, height: img.height });
        }
    }, [img, onLoad]);

    return (
        <KonvaImage
            image={img}
            width={width}
            height={height}
            listening={false} // Clicks pass through to images below
        />
    );
};

// -----------------------------------------------------------------------------
// Main Component
// -----------------------------------------------------------------------------

const ProductCustomizer = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useAppContext();
    const stageRef = useRef(null);
    const containerRef = useRef(null);

    // -- State --
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);

    // "Database" of user uploaded assets
    const [uploadedImages, setUploadedImages] = useState([]);

    // The pages of the calendar/product
    const [pages, setPages] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);

    // Canvas State: Map pageIndex -> { zoneId: { ...imageProps } }
    const [canvasState, setCanvasState] = useState({});
    const [selectedId, selectShape] = useState(null);

    // Dynamic Dims based on loaded overlay
    const [dynamicDims, setDynamicDims] = useState(null);
    const [scale, setScale] = useState(1);

    // Mobile sidebar state
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Touch drag state for mobile
    const [touchDragImage, setTouchDragImage] = useState(null);

    // Upload hint tooltip state
    const [showUploadHint, setShowUploadHint] = useState(true);

    // Dropzone for sidebar
    const onDrop = (acceptedFiles) => {
        const newImages = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }));
        setUploadedImages(prev => [...prev, ...newImages]);
        setShowUploadHint(false); // Hide hint after first upload
    };
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, accept: { 'image/*': [] } });

    // -- Effects --
    useEffect(() => {
        fetchProduct();
    }, [productId]);

    // Auto-hide upload hint after 10 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowUploadHint(false);
        }, 10000); // 10 seconds

        return () => clearTimeout(timer);
    }, []);

    const fetchProduct = async () => {
        try {
            const response = await axios.get(`${API}/products/${productId}`);
            setProduct(response.data);
            initializeTemplate(response.data);
        } catch (error) {
            console.error("Error fetching product", error);
            toast.error("Could not load product");
        } finally {
            setLoading(false);
        }
    };

    const initializeTemplate = (productData) => {
        const config = productData.customization_config || getDefaultTemplate();
        setPages(config.pages || []);
    };

    const getDefaultTemplate = () => {
        // Mock template fallback
        return {
            pages: Array.from({ length: 12 }, (_, i) => ({
                id: `page-${i}`,
                name: new Date(0, i).toLocaleString('default', { month: 'long' }),
                base_width: 800,
                base_height: 600,
                layers: []
            }))
        };
    };

    // -- Scale Calculation Effect --
    const activePageConfig = pages[currentPage] || {};
    // Preference order: Dynamic (from Loaded Image) -> Config -> Default
    const stageWidth = dynamicDims?.width || activePageConfig?.base_width || 800;
    const stageHeight = dynamicDims?.height || activePageConfig?.base_height || 600;

    useEffect(() => {
        const handleResize = () => {
            if (!containerRef.current) return;
            const { offsetWidth, offsetHeight } = containerRef.current;

            // Adaptive padding based on screen size
            const padding = window.innerWidth < 768 ? 16 : 40; // Less padding on mobile
            const availW = offsetWidth - padding;
            const availH = offsetHeight - padding;

            if (stageWidth > 0 && stageHeight > 0) {
                const scaleW = availW / stageWidth;
                const scaleH = availH / stageHeight;
                // Fit ensuring full visibility
                const newScale = Math.min(scaleW, scaleH, 1);
                setScale(newScale);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [stageWidth, stageHeight]);


    // -- Canvas Interactions --

    const handleDragStart = (e, imgUrl) => {
        e.dataTransfer.setData('imageUrl', imgUrl);
    };

    const handleDropOnStage = (e) => {
        e.preventDefault();

        const imgUrl = e.dataTransfer.getData('imageUrl');
        if (!imgUrl) return;

        selectShape(null); // deselect others

        // Use Konva's pointer position which is relative to the stage
        if (!stageRef.current) return;

        // Register pointer position
        stageRef.current.setPointersPositions(e);
        const point = stageRef.current.getPointerPosition();

        // Transform pointer to stage coords if needed (scale is handled largely by Konva but lets be safe)
        const stage = stageRef.current;
        const transform = stage.getAbsoluteTransform().copy();
        transform.invert();

        const ptr = stage.getPointerPosition();
        let pos = { x: 0, y: 0 };
        if (ptr) {
            pos = transform.point(ptr);
        } else {
            pos = { x: 100, y: 100 };
        }

        const imgObj = new window.Image();
        imgObj.src = imgUrl;

        imgObj.onload = () => {
            // Default scale down if huge
            const maxSide = 300;
            let w = imgObj.width;
            let h = imgObj.height;
            if (w > maxSide || h > maxSide) {
                const ratio = Math.min(maxSide / w, maxSide / h);
                w *= ratio;
                h *= ratio;
            }

            // Center image at drop point
            const x = pos.x - (w / 2);
            const y = pos.y - (h / 2);

            const newImageState = {
                src: imgUrl,
                x,
                y,
                width: w,
                height: h,
                id: `img-${Date.now()}`
            };

            setCanvasState(prev => ({
                ...prev,
                [currentPage]: {
                    ...(prev[currentPage] || {}),
                    [`img-${Date.now()}`]: newImageState
                }
            }));

            selectShape(`img-${Date.now()}`);
        };
    };

    const checkDeselect = (e) => {
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            selectShape(null);
        }
    };

    // -- Touch Event Handlers for Mobile --
    const handleTouchStart = (e, imgUrl) => {
        e.preventDefault();
        setTouchDragImage(imgUrl);
    };

    const handleTouchMove = (e) => {
        e.preventDefault();
        // Visual feedback could be added here
    };

    const handleTouchEnd = (e) => {
        e.preventDefault();

        if (!touchDragImage) return;

        const touch = e.changedTouches[0];
        if (!touch) return;

        // Check if touch ended over the stage
        const stageContainer = stageRef.current?.container();
        if (!stageContainer) return;

        const rect = stageContainer.getBoundingClientRect();
        const touchX = touch.clientX;
        const touchY = touch.clientY;

        // Check if touch is within stage bounds
        if (
            touchX >= rect.left &&
            touchX <= rect.right &&
            touchY >= rect.top &&
            touchY <= rect.bottom
        ) {
            // Calculate position relative to stage
            const x = (touchX - rect.left) / scale;
            const y = (touchY - rect.top) / scale;

            // Load and add the image
            const imgObj = new window.Image();
            imgObj.src = touchDragImage;

            imgObj.onload = () => {
                // Default scale down if huge
                const maxSide = 300;
                let w = imgObj.width;
                let h = imgObj.height;
                if (w > maxSide || h > maxSide) {
                    const ratio = Math.min(maxSide / w, maxSide / h);
                    w *= ratio;
                    h *= ratio;
                }

                // Center image at touch point
                const imgX = x - (w / 2);
                const imgY = y - (h / 2);

                const newImageState = {
                    src: touchDragImage,
                    x: imgX,
                    y: imgY,
                    width: w,
                    height: h,
                    id: `img-${Date.now()}`
                };

                setCanvasState(prev => ({
                    ...prev,
                    [currentPage]: {
                        ...(prev[currentPage] || {}),
                        [`img-${Date.now()}`]: newImageState
                    }
                }));

                selectShape(`img-${Date.now()}`);

                // Close sidebar on mobile after adding image
                if (window.innerWidth < 768) {
                    setIsSidebarOpen(false);
                }
            };
        }

        setTouchDragImage(null);
    };

    const handleOverlayLoad = ({ width, height }) => {
        if (!dynamicDims || dynamicDims.width !== width || dynamicDims.height !== height) {
            setDynamicDims({ width, height });
        }
    };

    // -- Render --

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading Studio...</div>;
    if (!product) return <div>Product not found</div>;

    const activeElements = canvasState[currentPage] || {};
    const overlayLayer = activePageConfig?.layers?.find(l => l.type === 'overlay');

    return (
        <div className="h-screen w-screen flex flex-col bg-gray-100 overflow-hidden">

            {/* 1. Header */}
            <div className="h-14 md:h-16 bg-white border-b border-gray-200 flex items-center justify-between px-3 md:px-6 z-20 shadow-sm shrink-0">
                <div className="flex items-center gap-2 md:gap-4">
                    <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-full text-gray-600">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    {/* Mobile: Show sidebar toggle */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setIsSidebarOpen(!isSidebarOpen);
                                setShowUploadHint(false); // Hide hint when clicked
                            }}
                            className="md:hidden p-2 hover:bg-gray-100 rounded-full text-gray-600 relative"
                        >
                            <PhotoIcon className="h-5 w-5" />
                            {/* Pulsing ring animation */}
                            {showUploadHint && uploadedImages.length === 0 && (
                                <span className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75"></span>
                            )}
                        </button>

                        {/* Animated tooltip */}
                        {showUploadHint && uploadedImages.length === 0 && (
                            <div className="md:hidden absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 animate-bounce">
                                <div className="bg-blue-600 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
                                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-blue-600 rotate-45"></div>
                                    Click to upload images
                                </div>
                            </div>
                        )}
                    </div>
                    <div>
                        <h1 className="font-bold text-gray-900 text-sm md:text-base">{product.title}</h1>
                        <p className="text-xs text-gray-500 hidden sm:block">Customization Studio</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="text-xs md:text-sm font-medium text-gray-600 hidden sm:block">
                        Page {currentPage + 1} of {pages.length}
                    </div>
                    <div className="h-8 w-px bg-gray-300 mx-2 hidden sm:block" />
                    <button
                        onClick={async () => {
                            try {
                                console.log('Starting canvas capture...');
                                const designedPages = [];

                                // Get all pages with content
                                const pagesWithContent = [];
                                for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
                                    const pageElements = canvasState[pageIndex];
                                    if (pageElements && Object.keys(pageElements).length > 0) {
                                        pagesWithContent.push(pageIndex);
                                    }
                                }

                                console.log('Pages with content:', pagesWithContent);

                                if (pagesWithContent.length === 0) {
                                    toast.error("Please design at least one page before adding to cart");
                                    return;
                                }

                                // Show loading toast
                                const loadingToast = toast.loading('Capturing your design...');

                                // Capture each page
                                for (const pageIndex of pagesWithContent) {
                                    setCurrentPage(pageIndex);

                                    // Wait for React to update the DOM
                                    await new Promise(resolve => setTimeout(resolve, 500));

                                    if (stageRef.current) {
                                        try {
                                            const stage = stageRef.current;
                                            console.log('Capturing page:', pageIndex, 'Stage:', stage);

                                            // Get the data URL with maximum quality
                                            const dataURL = stage.toDataURL({
                                                pixelRatio: 3,  // 3x resolution for print quality
                                                mimeType: 'image/png',  // PNG for lossless quality
                                                quality: 1  // Maximum quality
                                            });

                                            console.log('Data URL length:', dataURL.length);
                                            console.log('Data URL preview:', dataURL.substring(0, 100));

                                            if (dataURL && dataURL.length > 100) {
                                                designedPages.push({
                                                    pageIndex,
                                                    pageName: pages[pageIndex]?.name || `Page ${pageIndex + 1}`,
                                                    imageData: dataURL
                                                });
                                                console.log('Successfully captured page:', pageIndex);
                                            } else {
                                                console.error('Invalid data URL for page:', pageIndex);
                                            }
                                        } catch (error) {
                                            console.error('Failed to capture page:', pageIndex, error);
                                        }
                                    } else {
                                        console.error('Stage ref not available for page:', pageIndex);
                                    }
                                }

                                toast.dismiss(loadingToast);

                                if (designedPages.length === 0) {
                                    toast.error("Failed to capture design. Please try again.");
                                    return;
                                }

                                console.log('Total pages captured:', designedPages.length);
                                console.log('Designed pages:', designedPages);

                                // Add to cart with all designed page images
                                addToCart(product.id, 1, null, designedPages);
                                toast.success(`Design saved! ${designedPages.length} page(s) added to cart`);
                                navigate('/cart');
                            } catch (error) {
                                console.error('Error in Add to Cart:', error);
                                toast.error('Failed to save design. Please try again.');
                            }
                        }}
                        className="bg-black text-white px-3 md:px-6 py-2 rounded-full font-bold text-xs md:text-sm hover:bg-gray-800 transition-colors flex items-center gap-2"
                    >
                        <ShoppingBagIcon className="h-4 w-4" />
                        <span className="hidden sm:inline">Add to Cart</span>
                    </button>
                </div>
            </div>

            {/* 2. Workspace Body */}
            <div className="flex-1 flex overflow-hidden relative">

                {/* Mobile Sidebar Backdrop */}
                {isSidebarOpen && (
                    <div
                        className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-20"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}

                {/* Sidebar: Asset Library */}
                <div className={`
                    w-72 bg-white border-r border-gray-200 flex flex-col z-30 shrink-0
                    md:relative md:translate-x-0
                    fixed inset-y-0 left-0 transition-transform duration-300
                    ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}>
                    <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="font-bold text-gray-900">My Photos</h3>
                            <button
                                onClick={() => setIsSidebarOpen(false)}
                                className="md:hidden p-1 hover:bg-gray-100 rounded"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="relative">
                            <div {...getRootProps()} className={`border-2 border-dashed rounded-xl p-4 md:p-6 text-center cursor-pointer transition-all ${isDragActive ? 'border-blue-500 bg-blue-50' : showUploadHint && uploadedImages.length === 0 ? 'border-blue-400 bg-blue-50 animate-pulse' : 'border-gray-300 hover:bg-gray-50'}`}>
                                <input {...getInputProps()} />
                                <CloudArrowUpIcon className={`h-6 md:h-8 w-6 md:w-8 mx-auto mb-2 ${showUploadHint && uploadedImages.length === 0 ? 'text-blue-500' : 'text-gray-400'}`} />
                                <p className={`text-xs ${showUploadHint && uploadedImages.length === 0 ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                                    {showUploadHint && uploadedImages.length === 0 ? '👆 Click here to upload images!' : 'Drop photos here or click to upload'}
                                </p>
                            </div>

                            {/* Desktop animated pointer */}
                            {showUploadHint && uploadedImages.length === 0 && (
                                <div className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 translate-x-full ml-2 animate-bounce">
                                    <div className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg shadow-lg whitespace-nowrap">
                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-blue-600 rotate-45"></div>
                                        Start by uploading your photos here!
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 gap-3 content-start">
                        {uploadedImages.map((img, i) => (
                            <div
                                key={i}
                                className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-grab active:cursor-grabbing hover:ring-2 ring-blue-500 touch-none"
                                draggable="true"
                                onDragStart={(e) => handleDragStart(e, img.preview)}
                                onTouchStart={(e) => handleTouchStart(e, img.preview)}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            >
                                <img src={img.preview} alt="upload" className="w-full h-full object-cover pointer-events-none" />
                            </div>
                        ))}
                        {uploadedImages.length === 0 && (
                            <div className="col-span-2 text-center py-10 text-gray-400 text-sm">
                                No photos yet.
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Canvas Area */}
                <div
                    ref={containerRef}
                    className="flex-1 bg-gray-100 relative flex items-center justify-center overflow-hidden p-2 md:p-8"
                    onDrop={handleDropOnStage}
                    onDragOver={(e) => e.preventDefault()}
                >
                    {/* The "Paper" Container */}
                    <div
                        className="bg-white shadow-2xl transition-all duration-300 origin-center"
                        style={{
                            width: stageWidth * scale,
                            height: stageHeight * scale
                        }}
                    >
                        <Stage
                            ref={stageRef}
                            width={stageWidth * scale}
                            height={stageHeight * scale}
                            scale={{ x: scale, y: scale }}
                            onMouseDown={checkDeselect}
                            onTouchStart={checkDeselect}
                        >
                            <Layer>
                                {/* 1. Background / Paper */}
                                <Rect width={stageWidth} height={stageHeight} fill="white" />

                                {/* 2. Drop Zone Placeholders */}
                                {activePageConfig?.layers?.filter(l => l.type === 'zone').map((zone, i) => (
                                    <Group key={`zone-${i}`} x={zone.x} y={zone.y}>
                                        <Rect
                                            width={zone.width}
                                            height={zone.height}
                                            fill="#f3f4f6"
                                            stroke="#9ca3af"
                                            strokeWidth={2}
                                            dash={[10, 10]}
                                        />
                                        <Text
                                            text="Drop Photo"
                                            width={zone.width}
                                            height={zone.height}
                                            align="center"
                                            verticalAlign="middle"
                                            fill="#9ca3af"
                                            fontSize={20}
                                            fontStyle="bold"
                                        />
                                    </Group>
                                ))}

                                {/* 3. User Images */}
                                {Object.entries(activeElements).map(([key, imgParams]) => (
                                    <URLImage
                                        key={key}
                                        image={imgParams}
                                        isSelected={key === selectedId}
                                        onSelect={() => selectShape(key)}
                                        onChange={(newAttrs) => {
                                            setCanvasState(prev => ({
                                                ...prev,
                                                [currentPage]: {
                                                    ...prev[currentPage],
                                                    [key]: newAttrs
                                                }
                                            }));
                                        }}
                                    />
                                ))}

                                {/* 4. Overlay (The Calendar Grid / Mask) */}
                                {overlayLayer && (
                                    <OverlayImage
                                        src={overlayLayer.src}
                                        width={stageWidth}
                                        height={stageHeight}
                                        onLoad={handleOverlayLoad}
                                    />
                                )}

                            </Layer>
                        </Stage>
                    </div>

                    {/* Page Navigation Overlay */}
                    <div className="absolute bottom-4 md:bottom-8 flex items-center gap-2 md:gap-4 bg-white px-3 md:px-4 py-2 rounded-full shadow-lg z-30">
                        <button
                            onClick={() => {
                                setCurrentPage(prev => Math.max(0, prev - 1));
                                setDynamicDims(null);
                            }}
                            disabled={currentPage === 0}
                            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full disabled:opacity-30"
                        >
                            <ChevronLeftIcon className="h-4 md:h-5 w-4 md:w-5" />
                        </button>
                        <span className="font-bold min-w-[2.5rem] md:min-w-[3rem] text-center text-xs md:text-sm">
                            {activePageConfig?.name || `Page ${currentPage + 1}`}
                        </span>
                        <button
                            onClick={() => {
                                setCurrentPage(prev => Math.min(pages.length - 1, prev + 1));
                                setDynamicDims(null);
                            }}
                            disabled={currentPage === pages.length - 1}
                            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full disabled:opacity-30"
                        >
                            <ChevronRightIcon className="h-4 md:h-5 w-4 md:w-5" />
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProductCustomizer;
